import { FC, ReactNode, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../Dialog";
import { Separator } from "../Separator";
import { Badge } from "../Badge";
import { Button } from "../Button";
import dayjs from "dayjs";
import { SimpleActivity } from "@ac-expo/api-types";
import { isBefore } from "date-fns";

type Activity = {
  id?: string;
  name: string;
  description: string;
  startHour: string;
  endHour: string;
  day: string;
  isOwnStand: boolean | null;
  isRegistered?: boolean;
  isRegistrationRequired: boolean;
  areaName?: string;
};

type ActivityDetailsProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activities: SimpleActivity[];
  getActivityCartPart?: (activityId: string) => ReactNode;
  getRegisterButton?: (activityId: string) => ReactNode;
  searchBar?: ReactNode;
};
export const ActivityDetails: FC<ActivityDetailsProps> = ({
  open,
  onOpenChange,
  activities: activitiesData,
  getActivityCartPart,
  searchBar,
  getRegisterButton,
}) => {
  const [selectedActivity, setSelectedActivity] = useState<Activity | null>(
    null
  );
  const activities = activitiesData.map(formatActivity);

  const openSingleActivityDetails = (activityId: string) => {
    const activity = activities.find((a) => a.id === activityId);

    if (!activity) {
      return;
    }
    setSelectedActivity(activity);
    onOpenChange(true);
  };

  if (activities.length === 0) {
    return null;
  }

  if (activities.length === 1) {
    return (
      <SingleActivityDetails
        open={open}
        onOpenChange={onOpenChange}
        activity={activities[0]}
        getActivityCartPart={getActivityCartPart}
      />
    );
  }
  return (
    <>
      <ActivitiesList
        open={open}
        onOpenChange={onOpenChange}
        openSingleActivityDetails={openSingleActivityDetails}
        activities={activities}
        searchBar={searchBar}
        getRegisterButton={getRegisterButton}
      />
      {selectedActivity !== null ? (
        <SingleActivityDetails
          open={true}
          onOpenChange={(open) => !open && setSelectedActivity(null)}
          activity={selectedActivity}
          getActivityCartPart={getActivityCartPart}
        />
      ) : null}
    </>
  );
};

type SingleActivityDetailsProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activity: Activity;
  getActivityCartPart?: (activityId: string) => ReactNode;
};
const SingleActivityDetails: FC<SingleActivityDetailsProps> = ({
  open,
  onOpenChange,
  activity,
  getActivityCartPart,
}) => {
  const { id, name, description, startHour, endHour, isOwnStand, areaName } =
    activity;

  const localAreaName = areaName ?? "";

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader className="py-1">
          <DialogTitle>Détail de l&apos;animation</DialogTitle>
        </DialogHeader>
        <Separator />
        <div className="flex flex-col gap-3">
          <div className="flex flex-row items-center justify-start gap-4">
            <div>
              <div>
                <p className="text-sm text-gray-600">
                  {startHour} - {endHour}
                </p>
              </div>
            </div>
            <div>{isOwnStand ? <Badge variant="pink">Stand</Badge> : null}</div>
            <div>
              {!isOwnStand && localAreaName ? (
                <Badge variant="red-orange">{localAreaName}</Badge>
              ) : null}
            </div>
          </div>
          <h3 className="text-lg font-extrabold leading-none tracking-tight">
            {name}
          </h3>
          <p className="text-sm text-gray-600">{description}</p>
          {getActivityCartPart?.(id ?? "")}
        </div>
      </DialogContent>
    </Dialog>
  );
};

type ActivitiesListProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  activities: Activity[];
  getRegisterButton?: (activityId: string) => ReactNode;
  searchBar?: ReactNode;
  openSingleActivityDetails: (activityId: string) => void;
};
const ActivitiesList: FC<ActivitiesListProps> = ({
  searchBar,
  open,
  onOpenChange,
  activities,
  openSingleActivityDetails,
  getRegisterButton,
}) => {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[calc(100vh-60px)] max-w-[calc(100vw-20px)] overflow-auto bg-white max-md:max-w-full md:max-h-[calc(100vh-120px)] md:max-w-[700px]">
        <DialogTitle>Rechercher une animation</DialogTitle>
        {searchBar}
        <Separator />
        <div className="flex max-h-[372px] flex-col gap-4 overflow-y-auto md:px-[42px]">
          {activities.length === 0 ? (
            <p className="text-primary-500 flex w-full justify-center py-10">
              Aucun résultat
            </p>
          ) : (
            activities.map((activity) => {
              const { startHour, endHour, day } = activity;
              const now = new Date();
              const endDateTime = dayjs(`${day} ${endHour}`).toDate();
              const isDisabled = isBefore(endDateTime, now);

              return (
                <Button
                  variant="null"
                  key={activity.id}
                  className="bg-primary-100 flex h-fit w-full justify-between rounded-lg px-4 py-2"
                  onClick={() => openSingleActivityDetails(activity.id ?? "")}
                  disabled={isDisabled}
                >
                  <div className="text-primary-500 flex flex-col text-start">
                    <div className="text-xs">
                      <p>{dayjs(day).format("ddd D MMMM")}</p>
                      <p>
                        {startHour} - {endHour}
                      </p>
                    </div>
                    <p className="text-primary-700 font-bold">
                      {activity.name}
                    </p>
                  </div>
                  <div>
                    {!activity.isRegistered &&
                    activity.isRegistrationRequired &&
                    getRegisterButton !== undefined
                      ? getRegisterButton(activity.id ?? "")
                      : null}
                  </div>
                </Button>
              );
            })
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

const formatActivity = (activity: SimpleActivity): Activity => {
  return {
    id: activity.id,
    name: activity.name,
    description: activity.description,
    startHour: activity.schedules[0].timetable.startHour,
    endHour: activity.schedules[0].timetable.endHour,
    day: activity.schedules[0].timetable.day,
    isOwnStand: activity.activityArea?.isOwnStand ?? null,
    isRegistered: activity.ticketTypes.some((tt) => tt.tickets.length > 0),
    isRegistrationRequired: activity.registrationRequired,
    areaName: activity.activityArea?.areaName,
  };
};
